import { ColumnBoxFlexFullWidth, FlexBox } from '../../../../common/boxes';
import { MenuItem, TextField, Typography } from '@mui/material';
import { IBaseExperience, IExperience } from '../../../../../interfaces/experience';
import { ChangeEvent, Dispatch, SetStateAction, useCallback } from 'react';
import { IEducation } from '../../../../../interfaces/education';
import { YearAutocomplete } from '../../../../common/YearAutocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { TEXTS } from '../../../../../core/texts';
import { ValidationErrorState } from '../../../../../types/validationErrorState';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

interface Props {
  state: IBaseExperience,
  setState: Dispatch<SetStateAction<IExperience | IEducation>>,
  validationError: ValidationErrorState
};
const monthsArr = Array(12).fill(null);

const Dates = ({ state, setState, validationError }: Props) => {
  const handleYearChange = useCallback((val: number, isEndYear?: boolean) => {
    if (!isEndYear) {
      if (!state.endDateYear || state.endDateYear > val) {
        return setState(prev => ({ ...prev, startDateYear: val }));
      }
      return setState(prev => ({
        ...prev,
        startDateYear: val,
        endDateYear: val,
        endDateMonth: (prev.endDateMonth! > prev.startDateMonth) ? prev.endDateMonth : prev.startDateMonth
      }));
    }

    if (state.startDateYear < val) {
      return setState(prev => ({ ...prev, endDateYear: val }));
    }

    setState(prev => ({
      ...prev,
      endDateYear: val,
      startDateYear: val,
      startDateMonth: (!!prev.endDateMonth && prev.endDateMonth < prev.startDateMonth) ? prev.endDateMonth : prev.startDateMonth
    }));
  }, [state, setState]);

  const handleMonthChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    const value = +e.target.value;
    if (state.startDateYear !== state.endDateYear) {
      return setState(prev => ({ ...prev, [name]: value }));
    }
    if (name.startsWith('start')) {
      return setState(prev => ({
        ...prev,
        [name]: value,
        endDateMonth: (!!prev.endDateMonth && prev.endDateMonth >= value) ? prev.endDateMonth : value
      }));
    }
    setState(prev => ({
      ...prev,
      [name]: value,
      startDateMonth: prev.startDateMonth <= value ? prev.startDateMonth : value
    }));
  }, [state, setState]);

  return (
    <ColumnBoxFlexFullWidth>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ColumnBoxFlexFullWidth>
          <Typography sx={{ textAlign: 'center', my: 2, mr: { mobile: 0, tablet: 2 } }}>
            {TEXTS.START_DATE}
          </Typography>
          <FlexBox flexGrow={1}>
            <TextField
              label={'Month'}
              select={true}
              name={'startDateMonth'}
              placeholder={'Month'}
              sx={{ flexGrow: 1, mr: 2 }}
              value={state.startDateMonth} onChange={handleMonthChange}
              SelectProps={{ MenuProps: { sx: { maxHeight: { mobile: 200, tablet: 300 } } } }}
            >
              {
                monthsArr.map((el, index) => (
                  <MenuItem key={index} value={index + 1}>
                    {MONTH_NAMES[index]}
                  </MenuItem>
                ))
              }
            </TextField>
            <YearAutocomplete onChange={handleYearChange} value={state.startDateYear} variant={'outlined'}
                              error={validationError.startDateYear}/>
          </FlexBox>
        </ColumnBoxFlexFullWidth>

        {
          !!state.endDateMonth && !!state.endDateYear &&
          <ColumnBoxFlexFullWidth mt={2}>
            <Typography sx={{ textAlign: 'center', my: 2, mr: { mobile: 0, tablet: 2 } }}>
              {TEXTS.END_DATE}
            </Typography>
            <FlexBox flexGrow={1}>
              <TextField
                label={'Month'}
                select={true}
                name={'endDateMonth'}
                placeholder={'Month'}
                sx={{ flexGrow: 1, mr: 2 }}
                value={state.endDateMonth} onChange={handleMonthChange}
                SelectProps={{ MenuProps: { sx: { maxHeight: { mobile: 200, tablet: 300 } } } }}
              >
                {
                  monthsArr.map((el, index) => (
                    <MenuItem key={index} value={index + 1}>
                      {MONTH_NAMES[index]}
                    </MenuItem>
                  ))
                }
              </TextField>

              <YearAutocomplete
                onChange={(year) => handleYearChange(year, true)}
                value={state.endDateYear} variant={'outlined'}
                error={validationError.endDateYear}/>
            </FlexBox>
          </ColumnBoxFlexFullWidth>
        }
      </LocalizationProvider>
    </ColumnBoxFlexFullWidth>
  );
};

export default Dates;